import { defaultSerializeQueryArgs, skipToken } from '@reduxjs/toolkit/query/react';
import { deepEqual, type Optional } from '@direct-frontend/stdlib';
import type { DataProvider } from '@adv-frontend/data-provider';
import {
    createFlow,
    computedFlow,
    asyncComputedFlow,
    type AsyncFlow,
    type Flow,
    type AsyncFlowComputationContext,
    type FlowComputationContext,
    type MutableFlow,
} from '@adv-frontend/modules-flow';
import { memoize } from '@adv-frontend/modules-memo';

import { UnexpectedError } from '@adv-frontend/error';
import type { AnyQueryDefinition, QueryEndpointMethodsAndHooks } from '../apiSSRModule';
import type { QueryArgFrom, ResultTypeFrom, SkipToken, TagDescription } from '../rtk-query-types';

import type {
    BaseRowData,
    CreateEndpointDataProviderPaginatorFlow,
    RequestedChunksFlows,
    UseEndpointDataProviderPaginatorOptions,
} from './types';
import { iterateOverChunks } from './lib/iterateOverChunks/iterateOverChunks';
import { memoizeTransformChunk } from './lib/memoizeTransformChunk/memoizeTransformChunk';

const PARAMS_COMPARING_MAX_DEPTH = 20;

function getPaginatorOptions<Definition extends AnyQueryDefinition>(
    requestedChunks: RequestedChunksFlows<Definition>,
    chunksData: ResultTypeFrom<Definition>[]
): UseEndpointDataProviderPaginatorOptions<Definition> {
    return {
        requestedChunks,
        cachedValues: chunksData.map(data => ({ data })),
    };
}

function getPaginatorOptionsFromSnapshot<Definition extends AnyQueryDefinition>(
    requestedChunksFlow: Flow<RequestedChunksFlows<Definition>>
): UseEndpointDataProviderPaginatorOptions<Definition> {
    const requestedChunks = requestedChunksFlow.getSnapshot();
    const cachedValues = requestedChunks.map(({ queryFlow }) => {
        const state = queryFlow.getSnapshot();
        if (state.status === 'error') {
            throw state.error;
        }
        return { data: state.data };
    });
    return { requestedChunks, cachedValues };
}

function getCachedValues<Definition extends AnyQueryDefinition>(
    requestedChunksFlow: Flow<RequestedChunksFlows<Definition>>
): ResultTypeFrom<Definition>[] {
    return getPaginatorOptionsFromSnapshot(requestedChunksFlow).cachedValues.flatMap(({ data }) =>
        data === undefined ? [] : [data]
    );
}

/**
 * Тип возвращаемых данных из flow-based endpoint-датапровайдера.
 */
export interface EndpointDataProvider<
    Definition extends AnyQueryDefinition = AnyQueryDefinition,
    RowData extends BaseRowData = BaseRowData,
> extends DataProvider<RowData> {
    /**
     * Поток запрошенных чанков.
     */
    requestedChunksFlow: Flow<RequestedChunksFlows<Definition>>;
    /**
     * Поток состояния, выполняется ли обновление данных для первого чанка.
     */
    isFetching: AsyncFlow<boolean>;
    /**
     * Возвращает список закешированных в датапровайдере значений
     */
    cachedValues: AsyncFlow<ResultTypeFrom<Definition>[]>;
    /**
     * Возвращает поток параметров запроса для строки.
     * @param rowIndex Номер строки.
     * @returns Поток параметров запроса или skipToken.
     */
    rowQueryArg: (rowIndex: number) => AsyncFlow<QueryArgFrom<Definition> | SkipToken>;
    /**
     * Обновляет данные строки вручную (оптимистичное обновление).
     * Метод следует вызывать только после загрузки строки.
     * @param rowIndex Номер строки.
     * @param updateRecipe Функция-рецепт для обновления данных строки (мутирует данные).
     * @returns Объект с методом `undo` для отмены изменений.
     */
    updateRowDataManual: (rowIndex: number, updateRecipe: (rowData: RowData) => void) => { undo: () => void };
}

/**
 * Базовая конфигурация flow-based провайдера.
 */
type BaseEndpointDataProviderConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
    TransformedChunk,
> = {
    /** Фабрика пагинатора, оборачивается в лямбду для корректного вывода типа PaginatorParams */
    getPaginatorFactory: () => CreateEndpointDataProviderPaginatorFlow<Definition, PaginatorParams>;
    /** Функция получения параметров запроса эндпоинта */
    getQueryArg(
        paginatorParams: NoInfer<PaginatorParams>,
        queryArg: QueryArg,
        cachedValues: ResultTypeFrom<Definition>[]
    ): QueryArgFrom<Definition> | SkipToken;
    /**
     * Функция подготовки данных чанка: вычисляется один раз на чанк, а ее результат приходит
     * первым аргументом в `getRowByIndex` вместо сырого ответа эндпоинта. Сюда выносится все,
     * что одинаково для всех строк чанка — например списки идентификаторов для батч-запросов.
     *
     * Результат обязан держать объекты чанка по ссылке, а не копировать их: `updateRowDataManual`
     * вызывает трансформацию на immer-черновике, и мутации копий в черновик не попадут.
     */
    transformChunk?(chunkData: ResultTypeFrom<Definition>, queryArg: QueryArg): TransformedChunk;
    /**
     * Функция получения строки из подготовленных данных чанка (из ответа эндпоинта, если `transformChunk` не передан).
     * Третьим аргументом получает queryArg провайдера — строке может понадобиться, например,
     * чтобы построить вложенный поток за дополнительными данными по параметрам запроса.
     */
    getRowByIndex(chunkData: NoInfer<TransformedChunk>, rowIndexInChunk: number, queryArg: QueryArg): Optional<RowData>;
    /**
     * Функция получения списка тегов, которые приводят к инвалидации строки.
     * Если передана, то провайдер переключается в режим построчной инвалидации данных. При построчной инвалидации,
     * в случае инвалидации чанка, в строке флаг `isFetching` будет равен `true` только если чанк был инвалидирован
     * из-за тега этой строки.
     */
    getRowInvalidatedBy?(row: RowData): TagDescription<string>[];
};

/**
 * Конфигурация flow-based провайдера для фиксированной генерации строк.
 */
type EndpointDataProviderWithTotalCountConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
    TransformedChunk,
> = BaseEndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk> & {
    /**
     * Функция получения общего количества строк из ответа эндпоинта
     *
     * @param chunkData Данные первого чанка
     * @param queryArg Параметры запроса, с которыми создан датапровайдер. Нужны, когда количество
     * строк известно не из ответа, а из самих параметров — например при выборке по списку идентификаторов
     */
    getTotalCount(chunkData: ResultTypeFrom<Definition>, queryArg: QueryArg): number;
};

/**
 * Конфигурация flow-based провайдера для динамической генерации строк.
 */
type EndpointDataProviderWithoutTotalCountConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
    TransformedChunk,
> = BaseEndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk> & {
    /**
     * Функция получения количества строк в чанке из ответа эндпоинта
     */
    getRowsCount(chunkData: ResultTypeFrom<Definition>): number;
};

/**
 * Конфигурация flow-based провайдера.
 */
type EndpointDataProviderConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
    TransformedChunk,
> =
    | EndpointDataProviderWithTotalCountConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk>
    | EndpointDataProviderWithoutTotalCountConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk>;

/**
 * Параметры для функции, возвращаемой методом createEndpointDataProvider.
 */
type EndpointDataProviderOptions = {
    /**
     * Индекс первой строки.
     */
    initialRowIndex?: number;
};

type LoadedRowsFlowParams<Definition extends AnyQueryDefinition, PaginatorParams extends object, QueryArg> = {
    cachedValuesFlow: AsyncFlow<ResultTypeFrom<Definition>[]>;
    /** Поток общего количества строк. */
    totalCountFlow: AsyncFlow<number>;
    createPaginator: CreateEndpointDataProviderPaginatorFlow<Definition, PaginatorParams>;
    /** Поток запрошенных чанков. */
    requestedChunksFlow: Flow<RequestedChunksFlows<Definition>>;
    typedQueryArg: QueryArg;
};

type ChunkDataFlowParams<Definition extends AnyQueryDefinition, PaginatorParams extends object, QueryArg> = {
    chunkParams: PaginatorParams;
    startIndex: number;
    requestedChunksFlow: MutableFlow<RequestedChunksFlows<Definition>>;
    typedQueryArg: QueryArg;
};

type RowDataFlowParams<Definition extends AnyQueryDefinition, PaginatorParams extends object, QueryArg> = {
    /** Поток первого чанка */
    initialChunkFlow: AsyncFlow<RequestedChunksFlows<Definition>[number] | undefined>;
    rowsIdsFlow: AsyncFlow<string[]>;
    rowId: string;
    createPaginator: CreateEndpointDataProviderPaginatorFlow<Definition, PaginatorParams>;
    requestedChunksFlow: MutableFlow<RequestedChunksFlows<Definition>>;
    typedQueryArg: QueryArg;
};

type RowQueryArgFlowParams<Definition extends AnyQueryDefinition, PaginatorParams extends object, QueryArg> = {
    /** Поток первого чанка */
    initialChunkFlow: AsyncFlow<RequestedChunksFlows<Definition>[number] | undefined>;
    /** Индекс строки */
    rowIndex: number;
    /** Фабрика пагинатора */
    createPaginator: CreateEndpointDataProviderPaginatorFlow<Definition, PaginatorParams>;
    /** Поток запрошенных чанков */
    requestedChunksFlow: Flow<RequestedChunksFlows<Definition>>;
    /** Параметры запроса, с которыми создан датапровайдер */
    typedQueryArg: QueryArg;
};

type CachedValuesFlowParams<Definition extends AnyQueryDefinition> = {
    /** Поток первого чанка */
    initialChunkFlow: AsyncFlow<RequestedChunksFlows<Definition>[number] | undefined>;
    requestedChunksFlow: Flow<RequestedChunksFlows<Definition>>;
};

type IsFetchingFlowParams<Definition extends AnyQueryDefinition, RowData extends BaseRowData> = {
    /** Поток первого чанка */
    initialChunkFlow: AsyncFlow<RequestedChunksFlows<Definition>[number] | undefined>;
    /** Функция получения тегов, из-за которых строка инвалидируется */
    getRowInvalidatedBy?: (row: RowData) => TagDescription<string>[];
};

/**
 * Тип, который описывает параметр `createDataProvider` для query-эндпоинта.
 */
export type EndpointCreateDataProvider<Definition extends AnyQueryDefinition> = {
    createDataProvider: <
        PaginatorParams extends object,
        RowData extends BaseRowData,
        QueryArg,
        TransformedChunk = ResultTypeFrom<Definition>,
    >(
        config: EndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk>
    ) => (
        ...[queryArg]: undefined extends QueryArg
            ? [queryArg?: QueryArg, options?: EndpointDataProviderOptions]
            : [queryArg: QueryArg, options?: EndpointDataProviderOptions]
    ) => EndpointDataProvider<Definition, RowData>;
};

function isFlowDataProviderWithTotalCountConfig<
    Definition extends AnyQueryDefinition,
    RowData extends BaseRowData,
    PaginatorParams extends object,
    QueryArg,
    TransformedChunk,
>(
    config: EndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk>
): config is EndpointDataProviderWithTotalCountConfig<
    Definition,
    PaginatorParams,
    RowData,
    QueryArg,
    TransformedChunk
> {
    return 'getTotalCount' in config;
}

/**
 * Метод для генерации createDataProvider метода эндпоинта.
 * В отличие от buildCreateUseEndpointDataProvider, использует flow-примитивы
 * вместо React-хуков и возвращает DataProvider (UnstableDataProvider).
 */
export function buildCreateEndpointDataProvider<EndpointName extends string, Definition extends AnyQueryDefinition>(
    endpoint: {
        name: EndpointName;
        queryFlow: QueryEndpointMethodsAndHooks<Definition>['queryFlow'];
        updateQueryData: QueryEndpointMethodsAndHooks<Definition>['updateQueryData'];
    },
    definition: Definition
): EndpointCreateDataProvider<Definition>['createDataProvider'] {
    function serializeEndpointQueryArg(endpointQueryArg: QueryArgFrom<Definition> | typeof skipToken): string {
        const serializeResult = definition.serializeQueryArgs?.({
            queryArgs: endpointQueryArg,
            endpointDefinition: definition,
            endpointName: endpoint.name,
        });

        // Если `serializeQueryArgs` возвращает строку, то используем ее как ключ
        if (typeof serializeResult === 'string') {
            return serializeResult;
        }

        // Иначе используем дефолтный rtk-query сериализатор
        return defaultSerializeQueryArgs({
            // Передаем результат `serializeQueryArgs` если функция определена,
            // либо все queryArgs если не определена
            queryArgs: serializeResult ?? endpointQueryArg,
            endpointDefinition: definition,
            endpointName: endpoint.name,
        });
    }

    return function createDataProvider(config) {
        const { getPaginatorFactory, getQueryArg, getRowByIndex, getRowInvalidatedBy, transformChunk } = config;

        type PaginatorParams = Parameters<typeof getQueryArg>[0];
        type QueryArg = Parameters<typeof getQueryArg>[1];
        type TransformedChunk = Parameters<typeof getRowByIndex>[0];
        type RowData = NonNullable<ReturnType<typeof getRowByIndex>>;
        type GetRowByIndexBound = (chunkData: ResultTypeFrom<Definition>, rowIndexInChunk: number) => Optional<RowData>;

        const createPaginator = getPaginatorFactory();
        const getTransformedChunk = transformChunk ? memoizeTransformChunk(transformChunk) : undefined;

        /**
         * Собирает двухаргументную функцию чтения строки для точек, где queryArg уже известен:
         * подготовка данных чанка (один раз на чанк) плюс сборка строки.
         */
        const bindGetRowByIndex = (typedQueryArg: QueryArg): GetRowByIndexBound => {
            if (!getTransformedChunk) {
                return (chunkData, rowIndexInChunk) =>
                    // Без transformChunk строка получает сырой ответ эндпоинта: в этом случае TransformedChunk
                    // равен ResultTypeFrom<Definition>, но внутри дженерика это не выводится.
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                    getRowByIndex(chunkData as TransformedChunk, rowIndexInChunk, typedQueryArg);
            }

            return (chunkData, rowIndexInChunk) =>
                getRowByIndex(getTransformedChunk(chunkData, typedQueryArg), rowIndexInChunk, typedQueryArg);
        };

        // Аргументы, по которым мемоизируется общий поток запрошенных чанков.
        type ChunksFlowArgs = { queryArg: QueryArg; initialRowIndex: number };

        const argsEquals = (a: ChunksFlowArgs, b: ChunksFlowArgs) =>
            a.initialRowIndex === b.initialRowIndex && deepEqual(a.queryArg, b.queryArg, PARAMS_COMPARING_MAX_DEPTH);

        const createRequestedChunk = (
            endpointQueryArg: QueryArgFrom<Definition>,
            serializedQueryArg: string,
            startIndex: number
        ) => {
            const queryFlow = endpoint.queryFlow(endpointQueryArg, { prefetch: false });
            return {
                queryArg: endpointQueryArg,
                serializedQueryArg,
                startIndex,
                queryFlow,
            };
        };

        /**
         * Мемоизированный по args поток запрошенных чанков (общий мутабельный стейт провайдера).
         * Возвращает один и тот же инстанс для структурно одинаковых queryArg + initialRowIndex,
         * поэтому потоки провайдера, построенные поверх него, делят общее состояние и не теряют
         * загруженные чанки.
         *
         * Мемоизируется именно поток, а не объект-обертка над ним: `memoize` держит
         * значение через `WeakRef`, а обертку никто не удерживал снаружи — потребители сразу
         * деструктурировали ее. Сборщик мусора мог собрать запись кеша при живом датапровайдере,
         * и следующий вызов создавал новый пустой поток с повторным запросом первого чанка
         * (DIRECT-327483). Сам поток удерживается инстансом датапровайдера, поэтому запись кеша
         * жива, пока жив хотя бы один датапровайдер с этим queryArg.
         */
        const getRequestedChunksFlow = memoize(
            (_chunksFlowArgs: ChunksFlowArgs) => createFlow<RequestedChunksFlows<Definition>>([]),
            { equals: argsEquals }
        );

        const initialChunkAsyncComputedFlow = asyncComputedFlow(
            function* (
                context: AsyncFlowComputationContext,
                params: {
                    initialRowIndex: number;
                    createPaginator: CreateEndpointDataProviderPaginatorFlow<Definition, PaginatorParams>;
                    requestedChunksFlow: MutableFlow<RequestedChunksFlows<Definition>>;
                    typedQueryArg: QueryArg;
                }
            ) {
                const requestedChunks = context.watch(params.requestedChunksFlow);
                const initialPaginator = params.createPaginator(
                    getPaginatorOptionsFromSnapshot(params.requestedChunksFlow)
                );
                const initialChunkParams = initialPaginator.getChunkParams(params.initialRowIndex);
                const initialRowIndexInChunk = initialPaginator.getRowIndexInChunk(
                    params.initialRowIndex,
                    initialChunkParams
                );
                const initialChunkStartIndex = params.initialRowIndex - initialRowIndexInChunk;
                const existingInitialChunk = requestedChunks.find(chunk => chunk.startIndex === initialChunkStartIndex);
                if (existingInitialChunk) {
                    return existingInitialChunk;
                }

                let chunksData = yield* context.watchAsync.all(requestedChunks.map(chunk => chunk.queryFlow));
                const currentRequestedChunks = context.watch(params.requestedChunksFlow);
                if (currentRequestedChunks !== requestedChunks) {
                    chunksData = yield* context.watchAsync.all(currentRequestedChunks.map(chunk => chunk.queryFlow));
                }

                const paginator = params.createPaginator(getPaginatorOptions(currentRequestedChunks, chunksData));
                const currentInitialChunkParams = paginator.getChunkParams(params.initialRowIndex);
                const currentInitialRowIndexInChunk = paginator.getRowIndexInChunk(
                    params.initialRowIndex,
                    currentInitialChunkParams
                );
                const currentInitialChunkStartIndex = params.initialRowIndex - currentInitialRowIndexInChunk;
                const currentInitialChunk = currentRequestedChunks.find(
                    chunk => chunk.startIndex === currentInitialChunkStartIndex
                );
                if (currentInitialChunk) {
                    return currentInitialChunk;
                }

                const initialEndpointQueryArg = getQueryArg(currentInitialChunkParams, params.typedQueryArg, []);
                if (initialEndpointQueryArg === skipToken) {
                    return undefined;
                }

                const initialChunk = createRequestedChunk(
                    initialEndpointQueryArg,
                    serializeEndpointQueryArg(initialEndpointQueryArg),
                    currentInitialChunkStartIndex
                );
                params.requestedChunksFlow.emit([...currentRequestedChunks, initialChunk]);
                return initialChunk;
            },
            {
                paramEquals: (a, b) =>
                    a.initialRowIndex === b.initialRowIndex &&
                    a.createPaginator === b.createPaginator &&
                    a.requestedChunksFlow === b.requestedChunksFlow &&
                    deepEqual(a.typedQueryArg, b.typedQueryArg, PARAMS_COMPARING_MAX_DEPTH),
            }
        );

        const rowsIdsComputedFlow = asyncComputedFlow(
            function* (
                { watchAsync, watch }: AsyncFlowComputationContext,
                {
                    createPaginator,
                    totalCountFlow,
                    requestedChunksFlow,
                    typedQueryArg,
                }: {
                    createPaginator: CreateEndpointDataProviderPaginatorFlow<Definition, PaginatorParams>;
                    totalCountFlow: AsyncFlow<number>;
                    requestedChunksFlow: MutableFlow<RequestedChunksFlows<Definition>>;
                    typedQueryArg: QueryArg;
                }
            ) {
                const totalCount = yield* watchAsync(totalCountFlow);
                const requestedChunks = watch(requestedChunksFlow);
                const chunksData = yield* watchAsync.all(requestedChunks.map(chunk => chunk.queryFlow));
                const paginator = createPaginator(getPaginatorOptions(requestedChunks, chunksData));
                const chunkLength = paginator.getLimit();

                // Формируем сперва not_loaded_${index}, потом уже заполняем id загруженных данных
                const result = Array(totalCount)
                    .fill(null)
                    .map((_, rowIndex) => `not_loaded_${rowIndex}`);

                iterateOverChunks(
                    requestedChunks,
                    chunksData.map(data => ({ data })),
                    (data, requestedChunk, rowIndexInChunk) => {
                        const rowIndex = requestedChunk.startIndex + rowIndexInChunk;
                        if (rowIndex < result.length) {
                            result[rowIndex] = data.id;
                        }
                    },
                    {
                        chunkLength,
                        getRowByIndex: bindGetRowByIndex(typedQueryArg),
                    },
                    (requestedChunk, rowIndexInChunk) => {
                        /**
                         * Проверяем существование строки в списке строк, полученных по totalCount
                         * Если она есть, значит мы запрашивали (ожидаем получить) для нее данные, но нам не удалось их загрузить,
                         * поэтому помечаем такую строку как ошибочно загруженную
                         */
                        if (result[requestedChunk.startIndex + rowIndexInChunk] !== undefined) {
                            result[requestedChunk.startIndex + rowIndexInChunk] =
                                `load-failed_${requestedChunk.startIndex + rowIndexInChunk}`;
                        }
                    }
                );
                return result;
            },
            {
                paramEquals: (a, b) =>
                    a.totalCountFlow === b.totalCountFlow &&
                    a.createPaginator === b.createPaginator &&
                    a.requestedChunksFlow === b.requestedChunksFlow,
            }
        );

        const loadedRowsComputedFlow = asyncComputedFlow<
            RowData[],
            LoadedRowsFlowParams<Definition, PaginatorParams, QueryArg>
        >(
            function* (
                { watch, watchAsync },
                {
                    cachedValuesFlow,
                    totalCountFlow,
                    createPaginator,
                    requestedChunksFlow,
                    typedQueryArg,
                }: LoadedRowsFlowParams<Definition, PaginatorParams, QueryArg>
            ) {
                yield* watchAsync(cachedValuesFlow);
                const totalCount = yield* watchAsync(totalCountFlow);
                const requestedChunks = watch(requestedChunksFlow);
                const cachedValues = yield* watchAsync.all(requestedChunks.map(chunk => chunk.queryFlow));
                const paginator = createPaginator(getPaginatorOptions(requestedChunks, cachedValues));
                const chunkLength = paginator.getLimit();

                const getRowByIndexBound = bindGetRowByIndex(typedQueryArg);

                const rows: RowData[] = [];
                for (let chunkIndex = 0; chunkIndex < cachedValues.length; chunkIndex += 1) {
                    const chunkData = cachedValues[chunkIndex];
                    const requestedChunk = requestedChunks[chunkIndex];
                    if (chunkData === undefined || requestedChunk === undefined) {
                        continue;
                    }
                    for (let rowIndexInChunk = 0; rowIndexInChunk < chunkLength; rowIndexInChunk += 1) {
                        const rowIndex = requestedChunk.startIndex + rowIndexInChunk;
                        if (rowIndex >= totalCount) {
                            continue;
                        }
                        const data = getRowByIndexBound(chunkData, rowIndexInChunk);

                        if (data) {
                            rows.push(data);
                        }
                    }
                }

                return rows;
            },
            {
                paramEquals: (a, b) =>
                    a.cachedValuesFlow === b.cachedValuesFlow &&
                    a.totalCountFlow === b.totalCountFlow &&
                    a.createPaginator === b.createPaginator &&
                    a.requestedChunksFlow === b.requestedChunksFlow &&
                    // queryArg сравниваем по значению: потребитель может передавать новый объект с тем же
                    // содержимым на каждый рендер — по ссылке это приводило бы к лишней пересборке строк.
                    deepEqual(a.typedQueryArg, b.typedQueryArg, PARAMS_COMPARING_MAX_DEPTH),
            }
        );

        const chunkDataAsyncComputedFlow = asyncComputedFlow<
            ResultTypeFrom<Definition> | undefined,
            ChunkDataFlowParams<Definition, PaginatorParams, QueryArg>
        >(
            function* (
                { signal, watch, watchAsync }: AsyncFlowComputationContext,
                {
                    chunkParams,
                    startIndex,
                    requestedChunksFlow,
                    typedQueryArg,
                }: ChunkDataFlowParams<Definition, PaginatorParams, QueryArg>
            ) {
                const requestedChunks = watch(requestedChunksFlow);
                const resolvedChunksData = yield* watchAsync.all(requestedChunks.map(chunk => chunk.queryFlow));
                if (signal.aborted) {
                    throw signal.reason;
                }
                const endpointQueryArg = getQueryArg(chunkParams, typedQueryArg, resolvedChunksData);

                if (endpointQueryArg === skipToken) {
                    return undefined;
                }

                const serialized = serializeEndpointQueryArg(endpointQueryArg);
                const currentRequestedChunks = watch(requestedChunksFlow);
                const existingChunk = currentRequestedChunks.find(chunk => chunk.serializedQueryArg === serialized);
                if (existingChunk) {
                    return yield* watchAsync(existingChunk.queryFlow);
                }

                const requestedChunk = createRequestedChunk(endpointQueryArg, serialized, startIndex);
                requestedChunksFlow.emit([...currentRequestedChunks, requestedChunk]);
                return yield* watchAsync(requestedChunk.queryFlow);
            },
            {
                paramEquals: (a, b) =>
                    a.startIndex === b.startIndex &&
                    a.requestedChunksFlow === b.requestedChunksFlow &&
                    deepEqual(a.chunkParams, b.chunkParams, PARAMS_COMPARING_MAX_DEPTH) &&
                    deepEqual(a.typedQueryArg, b.typedQueryArg, PARAMS_COMPARING_MAX_DEPTH),
            }
        );

        const rowDataComputedFlow = asyncComputedFlow<
            RowData,
            RowDataFlowParams<Definition, PaginatorParams, QueryArg>
        >(
            function* (
                { watch, watchAsync }: AsyncFlowComputationContext,
                {
                    initialChunkFlow,
                    rowsIdsFlow,
                    rowId,
                    createPaginator,
                    requestedChunksFlow,
                    typedQueryArg,
                }: RowDataFlowParams<Definition, PaginatorParams, QueryArg>
            ) {
                yield* watchAsync(initialChunkFlow);

                /**
                 * Сохраняем данные даже по not_loaded_index, чтобы не кидать ошибку о ненайденной строке,
                 * т.к. использующий код мог не сменить id на актуальный.
                 */
                let rowIndex: number;
                const isNotLoadedRowId = rowId.startsWith('not_loaded_');
                if (isNotLoadedRowId) {
                    const notLoadedRowId = rowId.slice('not_loaded_'.length);
                    rowIndex = parseInt(notLoadedRowId, 10);
                } else {
                    const rowIds = yield* watchAsync(rowsIdsFlow);
                    rowIndex = rowIds.indexOf(rowId);
                }

                if (rowIndex === -1) {
                    throw new UnexpectedError('Row not found', { cause: undefined, additional: { rowId } });
                }

                const requestedChunks = watch(requestedChunksFlow);
                const chunksData = yield* watchAsync.all(requestedChunks.map(chunk => chunk.queryFlow));
                const paginator = createPaginator(getPaginatorOptions(requestedChunks, chunksData));
                const chunkParams = paginator.getChunkParams(rowIndex);
                const rowIndexInChunk = paginator.getRowIndexInChunk(rowIndex, chunkParams);
                const chunkStartIndex = rowIndex - rowIndexInChunk;

                const chunkDataFlow = chunkDataAsyncComputedFlow({
                    chunkParams,
                    startIndex: chunkStartIndex,
                    requestedChunksFlow,
                    typedQueryArg,
                });

                const data = yield* watchAsync(chunkDataFlow);

                if (!data) {
                    throw new UnexpectedError('Chunk not loaded', {
                        cause: undefined,
                        additional: { rowIndexInChunk },
                    });
                }

                const rowData = bindGetRowByIndex(typedQueryArg)(data, rowIndexInChunk);
                if (rowData === undefined) {
                    throw new UnexpectedError('Row not found in chunk', {
                        cause: undefined,
                        additional: { rowIndexInChunk },
                    });
                }
                return rowData;
            },
            {
                paramEquals: (a, b) =>
                    a.initialChunkFlow === b.initialChunkFlow &&
                    a.rowId === b.rowId &&
                    a.rowsIdsFlow === b.rowsIdsFlow &&
                    a.requestedChunksFlow === b.requestedChunksFlow &&
                    a.createPaginator === b.createPaginator &&
                    deepEqual(a.typedQueryArg, b.typedQueryArg, PARAMS_COMPARING_MAX_DEPTH),
            }
        );

        const rowQueryArgComputedFlow = asyncComputedFlow<
            QueryArgFrom<Definition> | SkipToken,
            RowQueryArgFlowParams<Definition, PaginatorParams, QueryArg>
        >(
            function* (
                { watch, watchAsync },
                {
                    initialChunkFlow,
                    rowIndex,
                    createPaginator,
                    requestedChunksFlow,
                    typedQueryArg,
                }: RowQueryArgFlowParams<Definition, PaginatorParams, QueryArg>
            ) {
                yield* watchAsync(initialChunkFlow);
                const requestedChunks = watch(requestedChunksFlow);
                const cachedValues = yield* watchAsync.all(requestedChunks.map(chunk => chunk.queryFlow));
                const paginator = createPaginator(getPaginatorOptions(requestedChunks, cachedValues));
                const chunkParams = paginator.getChunkParams(rowIndex);
                return getQueryArg(chunkParams, typedQueryArg, cachedValues);
            },
            {
                paramEquals: (a, b) =>
                    a.initialChunkFlow === b.initialChunkFlow &&
                    a.rowIndex === b.rowIndex &&
                    a.createPaginator === b.createPaginator &&
                    a.requestedChunksFlow === b.requestedChunksFlow &&
                    deepEqual(a.typedQueryArg, b.typedQueryArg, PARAMS_COMPARING_MAX_DEPTH),
            }
        );

        const cachedValuesComputedFlow = asyncComputedFlow<
            ResultTypeFrom<Definition>[],
            CachedValuesFlowParams<Definition>
        >(
            function* (
                { watch, watchAsync },
                { initialChunkFlow, requestedChunksFlow }: CachedValuesFlowParams<Definition>
            ) {
                yield* watchAsync(initialChunkFlow);
                const requestedChunks = watch(requestedChunksFlow);
                return yield* watchAsync.all(requestedChunks.map(chunk => chunk.queryFlow));
            },
            {
                paramEquals: (a, b) =>
                    a.initialChunkFlow === b.initialChunkFlow && a.requestedChunksFlow === b.requestedChunksFlow,
            }
        );

        const isFetchingComputedFlow = asyncComputedFlow<boolean, IsFetchingFlowParams<Definition, RowData>>(
            function* (
                { watch, watchAsync },
                { initialChunkFlow, getRowInvalidatedBy }: IsFetchingFlowParams<Definition, RowData>
            ) {
                const initialChunk = yield* watchAsync(initialChunkFlow);
                if (!initialChunk) {
                    return false;
                }

                // Все элементы requestedChunksFlow создаются через createRequestedChunk, но публичный тип чанка
                // намеренно скрывает дополнительные методы RTK Query потока.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                const initialChunkQueryFlow = initialChunk.queryFlow as ReturnType<
                    QueryEndpointMethodsAndHooks<Definition>['queryFlow']
                >;
                const initialChunkRequestFlow: Flow<{
                    /** Теги, из-за которых выполняется запрос */
                    invalidatedBy?: readonly unknown[];
                } | null> = initialChunkQueryFlow.currentRequest;

                const currentRequest = watch(initialChunkRequestFlow);
                if (getRowInvalidatedBy && currentRequest?.invalidatedBy) {
                    return false;
                }

                yield* watchAsync(initialChunk.queryFlow);
                return false;
            },
            {
                paramEquals: (a, b) =>
                    a.initialChunkFlow === b.initialChunkFlow && a.getRowInvalidatedBy === b.getRowInvalidatedBy,
            }
        );

        return function getEndpointDataProvider(...[queryArg, { initialRowIndex = 0 } = {}]) {
            // TypeScript не может вывести из условного rest-типа, что `QueryArg | undefined = QueryArg` когда `undefined extends QueryArg`
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            const typedQueryArg = queryArg as QueryArg;

            const requestedChunksFlow = getRequestedChunksFlow({
                queryArg: typedQueryArg,
                initialRowIndex,
            });
            const initialChunkFlow = initialChunkAsyncComputedFlow({
                initialRowIndex,
                createPaginator,
                requestedChunksFlow,
                typedQueryArg,
            });

            const totalCountFlow = totalCountAsyncComputedFlow(
                isFlowDataProviderWithTotalCountConfig(config)
                    ? {
                          requestedChunksFlow,
                          // queryArg связываем здесь: он одинаков для всего инстанса провайдера,
                          // а пересчёт при его смене обеспечивает сравнение typedQueryArg в paramEquals
                          getTotalCount: (chunkData: ResultTypeFrom<Definition>) =>
                              config.getTotalCount(chunkData, typedQueryArg),
                          createPaginator,
                          initialChunkFlow,
                          typedQueryArg,
                      }
                    : {
                          requestedChunksFlow,
                          getRowsCount: config.getRowsCount,
                          createPaginator,
                          initialChunkFlow,
                          typedQueryArg,
                      }
            );

            const rowsIdsFlow = rowsIdsComputedFlow({
                totalCountFlow,
                requestedChunksFlow,
                createPaginator,
                typedQueryArg,
            });
            const cachedValuesFlow = cachedValuesComputedFlow({ initialChunkFlow, requestedChunksFlow });
            const loadedRowsFlow = loadedRowsComputedFlow({
                cachedValuesFlow,
                totalCountFlow,
                createPaginator,
                requestedChunksFlow,
                typedQueryArg,
            });
            const rowDataFlow = memoize(
                (id: string) =>
                    rowDataComputedFlow({
                        initialChunkFlow,
                        rowsIdsFlow,
                        rowId: id,
                        createPaginator,
                        requestedChunksFlow,
                        typedQueryArg,
                    }),
                // Пин выключен: фабрика своя у каждого инстанса датапровайдера, и запиненное значение
                // навсегда оставило бы запись кеша в контексте страницы
                { maxPinned: 0 }
            );
            const rowQueryArgFlow = memoize(
                (rowIndex: number) =>
                    rowQueryArgComputedFlow({
                        initialChunkFlow,
                        rowIndex,
                        createPaginator,
                        requestedChunksFlow,
                        typedQueryArg,
                    }),
                { maxPinned: 0 }
            );

            return {
                requestedChunksFlow,
                isFetching: isFetchingComputedFlow({ initialChunkFlow, getRowInvalidatedBy }),
                totalCount: totalCountFlow,
                rowsIds: rowsIdsFlow,
                loadedRowsData: loadedRowsFlow,
                rowQueryArg: rowQueryArgFlow,
                cachedValues: cachedValuesFlow,
                updateRowDataManual: (
                    rowIndex: number,
                    updateRecipe: (rowData: NonNullable<ReturnType<typeof getRowByIndex>>) => void
                ) => {
                    const paginator = createPaginator(getPaginatorOptionsFromSnapshot(requestedChunksFlow));
                    const chunkParams = paginator.getChunkParams(rowIndex);
                    const cachedValues = getCachedValues(requestedChunksFlow);
                    const endpointQueryArg = getQueryArg(chunkParams, typedQueryArg, cachedValues);
                    const { undo } = endpoint.updateQueryData(
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                        endpointQueryArg as QueryArgFrom<Definition>,
                        chunkData => {
                            const rowIndexInChunk = paginator.getRowIndexInChunk(rowIndex, chunkParams);
                            const data = chunkData
                                ? bindGetRowByIndex(typedQueryArg)(chunkData, rowIndexInChunk)
                                : undefined;
                            if (data) {
                                updateRecipe(data);
                            }
                        }
                    );
                    return { undo };
                },
                rowData: rowDataFlow,
            };
        };
    };
}

type TotalCountFlowBaseParams<Definition extends AnyQueryDefinition, Params extends object, QueryArg> = {
    requestedChunksFlow: Flow<RequestedChunksFlows<Definition>>;
    createPaginator: CreateEndpointDataProviderPaginatorFlow<Definition, Params>;
    initialChunkFlow: AsyncFlow<RequestedChunksFlows<Definition>[number] | undefined>;
    typedQueryArg: QueryArg;
};

type TotalCountFlowWithTotalCountParams<
    Definition extends AnyQueryDefinition,
    Params extends object,
    QueryArg,
> = TotalCountFlowBaseParams<Definition, Params, QueryArg> & {
    getTotalCount: (queryResult: ResultTypeFrom<Definition>) => number;
};

type TotalCountFlowWithRowsCountParams<
    Definition extends AnyQueryDefinition,
    Params extends object,
    QueryArg,
> = TotalCountFlowBaseParams<Definition, Params, QueryArg> & {
    getRowsCount: (chunkData: ResultTypeFrom<Definition>) => number;
};

type TotalCountFlowParams<Definition extends AnyQueryDefinition, Params extends object, QueryArg> =
    | TotalCountFlowWithTotalCountParams<Definition, Params, QueryArg>
    | TotalCountFlowWithRowsCountParams<Definition, Params, QueryArg>;

function isTotalCountFlowWithTotalCount<Definition extends AnyQueryDefinition, Params extends object, QueryArg>(
    params: TotalCountFlowParams<Definition, Params, QueryArg>
): params is TotalCountFlowWithTotalCountParams<Definition, Params, QueryArg> {
    return 'getTotalCount' in params;
}

const totalCountAsyncComputedFlow = asyncComputedFlow(
    function* <Definition extends AnyQueryDefinition, Params extends object, QueryArg>(
        { watch, watchAsync }: AsyncFlowComputationContext,
        params: TotalCountFlowParams<Definition, Params, QueryArg>
    ) {
        const { requestedChunksFlow, createPaginator, initialChunkFlow } = params;
        const initialChunk = yield* watchAsync(initialChunkFlow);
        if (!initialChunk) {
            return 0;
        }

        let totalCount = 0;
        // Подписываемся на первый чанк в корне, чтобы при dynamic отловить ошибку чанка и не отправлять невалидный totalCount
        const initialQueryResult = yield* watchAsync(initialChunk.queryFlow);
        const requestedChunks = watch(requestedChunksFlow);
        const chunksData = yield* watchAsync.all(requestedChunks.map(chunk => chunk.queryFlow));
        const paginator = createPaginator(getPaginatorOptions(requestedChunks, chunksData));
        if (isTotalCountFlowWithTotalCount(params)) {
            // Статичный параметр
            const { getTotalCount } = params;
            totalCount = getTotalCount(initialQueryResult);
        } else {
            // Динамичный параметр
            const { getRowsCount } = params;
            const hasMoreMethod = paginator.hasMore;
            const chunkLength = paginator.getLimit();

            const hasMore = (chunkData: ResultTypeFrom<Definition>) => {
                if (hasMoreMethod) {
                    return hasMoreMethod(chunkData);
                }

                return getRowsCount(chunkData) >= chunkLength;
            };
            // Находим чанк последний по индексу в списке данных, а не по порядку загрузки чанков.
            // Пример: если загружены чанки [0, 1, 2, 9, 3, 4], то последним по индексу будет 9.
            const lastRequestedChunk = watch(lastChunkFlow({ requestedChunksFlow }));

            // Если нет запрошенного чанка, то возвращаем длину одного чанка, чтобы отрисовать стабы.
            if (!lastRequestedChunk) {
                totalCount = chunkLength;
            } else {
                const lastChunkData = yield* watchAsync(lastRequestedChunk.queryFlow);
                if (!hasMore(lastChunkData)) {
                    // Если чанк загрузили и это последний чанк - нашли итоговый totalCount
                    totalCount = lastRequestedChunk.startIndex + getRowsCount(lastChunkData);
                } else {
                    // Иначе - предполагаем, что не последний, добавляем еще один чанк помимо текущего
                    totalCount = lastRequestedChunk.startIndex + chunkLength * 2;
                }
            }
        }

        return totalCount;
    },
    {
        paramEquals: (a, b) =>
            a.initialChunkFlow === b.initialChunkFlow &&
            a.requestedChunksFlow === b.requestedChunksFlow &&
            a.createPaginator === b.createPaginator &&
            // queryArg участвует в подсчёте, поэтому сравниваем и его — по значению,
            // так как потребитель может передавать новый объект с тем же содержимым на каждый рендер
            deepEqual(a.typedQueryArg, b.typedQueryArg, PARAMS_COMPARING_MAX_DEPTH),
    }
);

type LastChunkFlowParams<Definition extends AnyQueryDefinition> = {
    requestedChunksFlow: Flow<RequestedChunksFlows<Definition>>;
};

/**
 * Хелпер получает последний запрошенный чанк и данные из кеша.
 *
 * @example
 * ```ts
 * const { lastRequestedChunk, lastCachedData } = lastChunkFlow({requestedChunksFlow: [chunk1, chunk3, chunk2]});
 * // lastRequestedChunk = chunk3, lastCachedData = data3
 * ```
 * @param requestedChunksFlow - Flow с запрошенными чанками.
 */
const lastChunkFlow = computedFlow(
    function <Definition extends AnyQueryDefinition>(
        { watch }: FlowComputationContext,
        { requestedChunksFlow }: LastChunkFlowParams<Definition>
    ) {
        const requestedChunks = watch(requestedChunksFlow);

        const lastRequestedChunk = requestedChunks.reduce<RequestedChunksFlows<Definition>[number] | null>(
            (lastRequestedChunk, chunk) => {
                if (!lastRequestedChunk) {
                    return chunk;
                }
                if (chunk.startIndex > lastRequestedChunk.startIndex) {
                    return chunk;
                }
                return lastRequestedChunk;
            },
            null
        );

        return lastRequestedChunk;
    },
    {
        paramEquals: <Definition extends AnyQueryDefinition>(
            a: LastChunkFlowParams<Definition>,
            b: LastChunkFlowParams<Definition>
        ) => a.requestedChunksFlow === b.requestedChunksFlow,
    }
);
